import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { signalsApi, type TradeThread, type Signal, type SignalEdit } from '~/lib/api';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { clsx } from 'clsx';
import { useAuth } from '~/contexts/AuthContext';
import { EditSignalModal } from './EditSignalModal';
import { PositionStatusLabel } from './PositionStatusLabel';

interface TradeThreadRowProps {
  thread: TradeThread;
  // Live option premium for OPEN positions (Massive). Drives unrealized %.
  livePremium?: { price: number; changePercent: number | null } | null;
}

export function TradeThreadRow({ thread, livePremium }: TradeThreadRowProps) {
  const [expanded, setExpanded] = useState(false);

  // Format timestamp from DB (UTC but missing Z suffix)
  const formatTime = (timestamp: string) => {
    const utcTimestamp = timestamp.replace(' ', 'T') + 'Z';
    const date = new Date(utcTimestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const isClosed = thread.thread_status === 'closed';
  const hasProfit = thread.pnl_percent !== null && thread.pnl_percent !== undefined && thread.pnl_percent >= 0;
  const ts = thread.last_action_at ?? thread.updated_at;

  return (
    <div>
      <div
        className="flex items-center gap-2.5 sm:gap-4 px-2.5 sm:px-4 py-2.5 hover:bg-surface-2/40 cursor-pointer transition-colors select-none"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Trader + last-action time. min-w-0 + truncate so a long name
            shrinks instead of forcing the row to overflow (no h-scroll).
            stopPropagation so the name link doesn't ALSO toggle the row. */}
        <div className="w-24 sm:w-40 shrink-0 min-w-0">
          {thread.trader_id ? (
            <Link
              to={`/traders/${thread.trader_id}`}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className="font-medium text-text-primary text-sm sm:text-base hover:text-accent transition-colors block truncate"
            >
              {thread.trader_username || 'Unknown'}
            </Link>
          ) : (
            <span className="font-medium text-text-primary text-sm sm:text-base block truncate">
              {thread.trader_username || 'Unknown'}
            </span>
          )}
          <div
            className="text-[10px] sm:text-xs text-text-muted truncate"
            title={new Date(ts.replace(' ', 'T') + 'Z').toLocaleString()}
          >
            {formatTime(ts)}
          </div>
        </div>

        {/* Symbol — flexes to fill, expiry stacked underneath. */}
        <div className="min-w-0 flex-1">
          <div className="font-medium font-mono text-text-primary text-sm sm:text-base truncate">
            {thread.ticker} {thread.strike}{thread.option_type}
          </div>
          <div className="text-[10px] sm:text-xs text-text-muted truncate">
            {thread.expiry || (thread.expiry_type === '0dte' ? '0DTE' : '')}
          </div>
        </div>

        {/* Entry → exit flow. Exit only shows ≥sm to keep mobile minimal. */}
        <div className="shrink-0 text-right font-mono text-xs sm:text-sm leading-tight tabular-nums">
          {thread.entry_price ? (
            <span className="text-text-secondary">${thread.entry_price.toFixed(2)}</span>
          ) : (
            <span className="text-text-muted">—</span>
          )}
          {thread.exit_price ? (
            <span className="hidden sm:inline text-text-muted"> → <span className="text-text-secondary">${thread.exit_price.toFixed(2)}</span></span>
          ) : null}
        </div>

        {/* Status + P/L, right-aligned. Closed → realized %, open → live
            unrealized % from the current option premium. */}
        <div className="shrink-0 text-right w-16 sm:w-20">
          <PositionStatusLabel status={thread.position_status} pnlPercent={thread.pnl_percent} />
          {isClosed && thread.pnl_percent !== null ? (
            <div className={clsx('text-xs font-mono font-semibold mt-0.5 tabular-nums', hasProfit ? 'text-profit' : 'text-loss')}>
              {hasProfit ? '+' : ''}{thread.pnl_percent.toFixed(0)}%
            </div>
          ) : !isClosed && livePremium?.price && thread.entry_price ? (() => {
            const unreal = ((livePremium.price - thread.entry_price) / thread.entry_price) * 100;
            return (
              <div className={clsx('text-xs font-mono font-semibold mt-0.5 tabular-nums', unreal >= 0 ? 'text-profit' : 'text-loss')} title={`Live ${livePremium.price.toFixed(2)}`}>
                {unreal >= 0 ? '+' : ''}{unreal.toFixed(0)}%
              </div>
            );
          })() : null}
        </div>
      </div>

      {expanded && (
        <div className="bg-surface-1 px-3 sm:px-4 py-3 sm:py-4 border-t border-border/40">
          <ExpandedThreadContent thread={thread} />
        </div>
      )}
    </div>
  );
}

function ExpandedThreadContent({ thread }: { thread: TradeThread }) {
  const { user, isAdmin } = useAuth();
  const [editingSignal, setEditingSignal] = useState<Signal | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['thread', thread.position_id],
    queryFn: () => signalsApi.threadSignals(thread.position_id),
  });

  const signals = data?.signals || [];
  const edits = data?.edits || [];

  // Find the opening signal (BTO or watch) for editing and P/L
  const entrySignal = signals.find((s) => s.action === 'BTO') || signals.find((s) => s.action === 'watch');
  const exitSignal = signals.find((s) => s.action === 'STC');
  const plData = entrySignal && exitSignal && entrySignal.signal_price > 0
    ? {
        entry: entrySignal.signal_price,
        exit: exitSignal.signal_price,
        plDollar: exitSignal.signal_price - entrySignal.signal_price,
        plPercent: ((exitSignal.signal_price - entrySignal.signal_price) / entrySignal.signal_price) * 100,
      }
    : null;

  // Check if current user can edit this trade (owner or admin). Compare the
  // Discord display names — server side enforces strict author_discord_id
  // match, this is just the UX hint for whether to surface the Edit button.
  const isOwner = !!user?.username && user.username === thread.trader_username;
  const canEdit = isOwner || isAdmin;

  if (isLoading) {
    return <div className="text-text-muted text-sm">Loading timeline...</div>;
  }

  return (
    <div>
      {editingSignal && (
        <EditSignalModal
          signal={editingSignal}
          positionId={thread.position_id}
          positionStatus={thread.position_status}
          positionEntryPrice={thread.entry_price}
          onClose={() => setEditingSignal(null)}
        />
      )}

      {signals.length > 0 && (
        <div>
          <div className="space-y-2">
            <ChronologicalTimeline signals={signals} edits={edits} />
          </div>
          {plData && (
            <div className="mt-3 pt-2 border-t border-border flex items-center gap-4 text-sm">
              <span className="text-text-muted">P/L:</span>
              <span
                className={clsx(
                  'font-medium font-mono',
                  plData.plPercent >= 0 ? 'text-profit' : 'text-loss'
                )}
              >
                {plData.plPercent >= 0 ? '+' : ''}${plData.plDollar.toFixed(2)} ({plData.plPercent >= 0 ? '+' : ''}{plData.plPercent.toFixed(1)}%)
              </span>
            </div>
          )}
          <div className="mt-3 pt-2 border-t border-border flex items-center gap-3">
            <Link
              to={`/plays/${thread.position_id}`}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className="text-accent text-sm hover:underline font-mono"
            >
              View full context →
            </Link>
            {canEdit && entrySignal && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingSignal(entrySignal);
                }}
                className="ml-auto px-4 py-1.5 rounded text-sm font-medium bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
              >
                Edit Signal
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Format field name for display */
function formatFieldName(field: string): string {
  const names: Record<string, string> = {
    ticker: 'Ticker',
    strike: 'Strike',
    option_type: 'Type',
    expiry: 'Expiry',
    expiry_type: 'Expiry Type',
    signal_price: 'Entry Price',
    position_status: 'Status',
  };
  return names[field] || field;
}

/** Format field value for display */
function formatFieldValue(field: string, value: string | null): string {
  if (value === null || value === '') return '-';
  if (field === 'option_type') return value === 'C' ? 'Call' : 'Put';
  if (field === 'strike' || field === 'signal_price') return `$${parseFloat(value).toFixed(2)}`;
  if (field === 'position_status') return value.charAt(0).toUpperCase() + value.slice(1);
  return value;
}

type TimelineEntry =
  | { type: 'signal'; data: Signal; timestamp: Date }
  | { type: 'edit'; data: SignalEdit; timestamp: Date };

function ChronologicalTimeline({ signals, edits }: { signals: Signal[]; edits: SignalEdit[] }) {
  // Combine signals and edits into a single chronologically sorted array
  const entries: TimelineEntry[] = [
    ...signals.map((signal) => ({
      type: 'signal' as const,
      data: signal,
      timestamp: new Date(signal.created_at.replace(' ', 'T') + 'Z'),
    })),
    ...edits.map((edit) => ({
      type: 'edit' as const,
      data: edit,
      timestamp: new Date(edit.edited_at.replace(' ', 'T') + 'Z'),
    })),
  ].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  return (
    <>
      {entries.map((entry) =>
        entry.type === 'signal' ? (
          <SignalTimelineItem key={entry.data.id} signal={entry.data} time={entry.timestamp} />
        ) : (
          <EditTimelineItem key={entry.data.id} edit={entry.data} time={entry.timestamp} />
        )
      )}
    </>
  );
}

function SignalTimelineItem({ signal, time }: { signal: Signal; time: Date }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="pt-1">
        <div
          className={clsx(
            'w-2 h-2 rounded-full',
            signal.action === 'BTO' && 'bg-profit',
            signal.action === 'STC' && 'bg-loss',
            signal.action === 'trim' && 'bg-warning',
            signal.action === 'watch' && 'bg-blue-500',
            signal.action === 'none' && 'bg-text-muted'
          )}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'text-xs font-mono font-medium uppercase',
              signal.action === 'BTO' && 'text-profit',
              signal.action === 'STC' && 'text-loss',
              signal.action === 'trim' && 'text-warning',
              signal.action === 'watch' && 'text-blue-400',
              signal.action === 'none' && 'text-text-secondary'
            )}
          >
            {signal.action}
          </span>
          {signal.signal_price > 0 && (
            <span className="text-text-secondary font-mono">${signal.signal_price.toFixed(2)}</span>
          )}
          <span className="text-text-muted text-xs">
            {time.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        {signal.message_content && (
          <p className="text-text-muted text-xs whitespace-pre-wrap break-words">{signal.message_content}</p>
        )}
      </div>
    </div>
  );
}

function EditTimelineItem({ edit, time }: { edit: SignalEdit; time: Date }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="pt-1">
        <div className="w-2 h-2 rounded-full bg-purple-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-medium text-purple-400">EDIT</span>
          <span className="text-text-secondary text-xs">
            {formatFieldName(edit.field)}: {formatFieldValue(edit.field, edit.old_value)} → {formatFieldValue(edit.field, edit.new_value)}
          </span>
          <span className="text-text-muted text-xs">
            {time.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <p className="text-text-muted text-xs">by {edit.edited_by}</p>
      </div>
    </div>
  );
}
